import React, { Fragment, useRef } from "react";
import { getThemedStyles, styles } from "./Comment.styles";
import { classNames } from "../../../../../utils/common/styling";
import { ICommentProps } from "./Comment.types";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { formatDistance, isSameDay, isSameYear } from "date-fns";
import { formatDateTime } from "../../../../../features/Common/modules/DateTime/DateTime.utils";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import Button from "../../../../common/Button/Button";
import CommenterImg from "../CommenterImg/CommenterImg";
import MoreButtonAndMenu from "../MoreButtonAndMenu/MoreButtonAndMenu";
import { useToggleState } from "../../../../../utils/common/dataTypes/boolean";
import LazyLoadedCommentEditor from "../CommentEditor/LazyLoadedCommentEditor";
import LazyLoadedDeleteConfirmationModal from "../../../../common/DeleteConfirmationModal/LazyLoadedDeleteConfirmationModal";
import { useLikedByMembersListModal } from "../../../../../utils/BrandedCommunity/LikedByUsersListModal/useLikedByUsersListModal";
import LazyLoadedLikedByMembersListModal from "../../../LikedByMembersListModal/LazyLoadedLikedByMembersListModal";
import useInsertTextAtCursor from "../../../../../features/Common/modules/Form/utils/useInsertTextAtCursor";
import ExlyImage from "../../../../../features/Common/modules/ExlyImage/ExlyImage";
import { ICommentEditor } from "../CommentEditor/CommentEditor.types";
import LazyLoadedImageViewerModal from "../../../../common/ImageViewerModal/LazyLoadedImageViewerModal";
import { useState } from "react";
import { USER_AVATAR_PREFERENCE } from "../CommenterImg/constants/CommenterImg.constants";
import { getUserInitials } from "../../../../../features/Common/modules/Comments/utils/Comments";
import { useCreatorThemeContext } from "../../../../../features/Common/modules/Theme/CreatorThemeContext";
import CreatorCommentSymbol from "../../../../../features/Common/modules/Comments/modules/CreatorCommentSymbol/CreatorCommentSymbol";
import { COMMENT_SOURCE } from "../../../../../features/Common/modules/Comments/constants/Comments";
import { FlaggedTag } from "../../modules/FlaggedTag/FlaggedTag";

const Comment = ({
  data,
  userUuid,
  isCreator,
  creatorUuid,
  userImgSrc,
  userAvatarPreference,
  fetchCdnAsset,
  enableHideUnhideComment,
  cdnBaseUrl,
  CommentEditor,
  onLikeClick: onLikeClickProp,
  callGetLikedByMembersListApi,
  onUpdate: onUpdateProp,
  onDelete,
  repliesChild,
  onReplyCtaClick,
  onMemberClick,
  canReport,
  onReport,
  showReported,
  CommentWrapper = "tr",
}: ICommentProps) => {
  const theme = useCreatorThemeContext();
  const themedStyles = getThemedStyles({ theme });
  const {
    profile_pic: commenterImgSrc,
    commenter_name: commenterName,
    comment: commentText,
    likes_count: likesCount = 0,
    attachments = [],
    is_flagged: isFlagged,
  } = data;
  const showFlagged = showReported && isFlagged;

  const currentUserIsCommentAuthor = userUuid === data.commenter_username;

  const isCommentFromCreator = creatorUuid === data.commenter_username;

  /**
   * if source is user Input or source is not defined in case of branded Community
   * else it will be  questionnaire response.
   */
  const isUserInputComment =
    !data?.source || data?.source == COMMENT_SOURCE.USER_INPUT;
  const canEdit = currentUserIsCommentAuthor && isUserInputComment;

  const canHideUnhide = (enableHideUnhideComment && isCreator) || false;

  const canDelete =
    (isCreator || // creator can delete any post
      currentUserIsCommentAuthor) &&
    isUserInputComment;

  const isReportingAllowed = canReport && userUuid !== data.commenter_username;

  const creatorIsCommentAuthor =
    // on CT side
    (isCreator && currentUserIsCommentAuthor) ||
    // on Webapp side
    creatorUuid === data.commenter_username;
  const canClickOnCommentAuthor = !creatorIsCommentAuthor;

  const now = new Date();
  const createdAt = new Date(data.created_at);
  const isSameDayAsToday = isSameDay(now, createdAt);
  const isSameYearAsCurrent = isSameYear(now, createdAt);
  const createdAtString = isSameDayAsToday
    ? formatDistance(createdAt, now, { addSuffix: true })
    : formatDateTime(createdAt, {
        showTime: true,
        showYear: !isSameYearAsCurrent,
      });

  const onLikeClick = () => onLikeClickProp({ comment_uuid: data.uuid });
  const [
    deleteConfirmationModalIsShown,
    showDeleteConfirmationModal,
    hideDeleteConfirmationModal,
  ] = useToggleState();

  const [commentEditorIsShown, showCommentEditor, hideCommentEditor] =
    useToggleState();
  const [isImageViewerModalOpen, openImageViewerModal, closeImageViewerModal] =
    useToggleState(false);
  const [imageViewerModalSrc, setImageViewerModalSrc] = useState("");
  const commentEditorRef = useRef<HTMLInputElement>(null);
  const { insertTextAtCursor } = useInsertTextAtCursor({
    inputRef: commentEditorRef,
  });
  const onUpdate = ({
    attachments: updatedAttachments,
  }: ICommentEditor.IOnSubmitArgs) => {
    if (commentEditorRef?.current) {
      onUpdateProp({
        commentData: {
          ...data,
          comment: (commentEditorRef.current.value || "").trim(),
        },
        attachments: updatedAttachments,
        successCallback: hideCommentEditor,
      });
    }
  };

  /**
   * Functionality required to hide and unhide comments used by Creator.
   * This is done through updating comment with status hidden and shown and
   * the end user from backend doesnot send the comments with status as hidden.
   */
  const onHideUnhide = ({ status }: ICommentEditor.IOnHideUnhideArgs) => {
    onUpdateProp({
      status: status,
      commentData: data,
      attachments,
      successCallback: () => {},
    });
  };

  const onCloseImageViewerModal = () => {
    closeImageViewerModal();
    setImageViewerModalSrc("");
  };

  const {
    open: openLikedByMembersListModal,
    ...lazyLoadedLikedByMembersListModalProps
  } = useLikedByMembersListModal({
    userAvatarPreference,
    callGetLikedByMembersListApi: (args) =>
      callGetLikedByMembersListApi({ ...args, comment_uuid: data.uuid }),
  });

  const CommentEditorComponent = CommentEditor || LazyLoadedCommentEditor;

  const handleReport = () => {
    if (onReport) onReport(data);
  };

  return (
    <>
      <div className={styles.container}>
        <table className={styles.table}>
          <CommentWrapper>
            <td className={styles.picCol}>
              <div>
                <CommenterImg
                  avatarPreference={userAvatarPreference}
                  userDetails={{
                    profilePic: commenterImgSrc,
                    username: data?.commenter_username,
                    initials: getUserInitials(data?.commenter_name),
                  }}
                />
              </div>
            </td>
            <td className={styles.commentBubble}>
              <div className={styles.detailsAndReactionsAndReplies}>
                {commentEditorIsShown ? (
                  <CommentEditorComponent
                    defaultValue={data.comment}
                    defaultAttachments={data?.attachments}
                    cdnBaseUrl={cdnBaseUrl}
                    creatorTheme={useCreatorThemeContext()}
                    show={commentEditorIsShown}
                    inputRef={commentEditorRef}
                    insertTextAtCursor={insertTextAtCursor}
                    onSubmit={onUpdate}
                    commenterDetails={{
                      profilePicUrl: userImgSrc,
                      username: userUuid || "",
                    }}
                    userAvatarPreference={USER_AVATAR_PREFERENCE.NONE}
                  />
                ) : (
                  <div
                    className={classNames(
                      styles.detailsContainer,
                      showFlagged && styles.flaggedContainer,
                    )}
                  >
                    <div
                      className={classNames(
                        styles.triangle,
                        showFlagged && styles.flaggedTraingle,
                      )}
                    />
                    {showFlagged && <div className={styles.triangle2} />}
                    <div className={styles.header}>
                      <div
                        className={classNames(
                          styles.commenterName,
                          canClickOnCommentAuthor &&
                            styles.commenterNameClickable,
                        )}
                        title={commenterName}
                        onClick={() => {
                          if (!canClickOnCommentAuthor) return;

                          onMemberClick({
                            memberUuid: data.commenter_username,
                          });
                        }}
                      >
                        {commenterName}
                        {currentUserIsCommentAuthor && (
                          <span className={styles.authorIsCurrentUserIndicator}>
                            {" (You)"}
                          </span>
                        )}
                        <span className={styles.creatorCommentSymbolSpan}>
                          <CreatorCommentSymbol
                            isCommentFromCreator={isCommentFromCreator}
                            fetchCdnAsset={fetchCdnAsset}
                          />
                        </span>
                      </div>
                      <div className={styles.createdAtAndMoreIcon}>
                        <div
                          className={styles.createdAt}
                          title={createdAtString}
                        >
                          {createdAtString}
                        </div>
                        <div className={styles.moreIconContainer}>
                          <MoreButtonAndMenu
                            MoreButtonComponent={MoreVertIcon}
                            canEdit={canEdit}
                            onEditClick={showCommentEditor}
                            canDelete={canDelete}
                            onDeleteClick={showDeleteConfirmationModal}
                            currentCommentStatus={data?.status}
                            canHideUnhide={canHideUnhide}
                            onHideUnhideClick={onHideUnhide}
                            canReport={isReportingAllowed}
                            onReport={handleReport}
                            reportCtaText="Report comment"
                          />
                        </div>
                      </div>
                    </div>
                    {isUserInputComment ? (
                      <div className={styles.commentText}>
                        {commentText}
                        {attachments.length > 0 && (
                          <div className={styles.attachments}>
                            {attachments.map((attachment, index) => {
                              return (
                                <ExlyImage
                                  key={attachment.url + index}
                                  onClick={() => {
                                    setImageViewerModalSrc(attachment.url);
                                    openImageViewerModal();
                                  }}
                                  src={attachment.url}
                                  fetchWidth={200}
                                  className={styles.image}
                                  cdnBaseUrl={cdnBaseUrl || ""}
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        className={styles.questionnaireResponse}
                        dangerouslySetInnerHTML={{
                          __html: data.comment,
                        }}
                      />
                    )}
                  </div>
                )}
              </div>

              <div className={styles.reactionsStrip}>
                <div className={styles.reactionsStripLeft}>
                  <div className={styles.likesContainer}>
                    {data.has_liked_comment ? (
                      <FavoriteRoundedIcon
                        className={classNames(
                          styles.likeIcon,
                          themedStyles.likeIconWhenLikedByUser,
                        )}
                        fontSize="small"
                        onClick={onLikeClick}
                      />
                    ) : (
                      <FavoriteBorderRoundedIcon
                        className={classNames(
                          styles.likeIcon,
                          styles.likeIconWhenNotLikedByUser,
                        )}
                        fontSize="small"
                        onClick={onLikeClick}
                      />
                    )}
                    <div
                      className={styles.likesCount}
                      onClick={openLikedByMembersListModal}
                    >
                      {likesCount}
                    </div>
                  </div>
                  <Button
                    className={classNames(
                      styles.replyButton,
                      themedStyles.replyButton,
                    )}
                    onClick={onReplyCtaClick}
                    color="unset"
                    size="unset"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <mask
                        id="mask0_3324_15189"
                        style={{ maskType: "alpha" }}
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width="20"
                        height="20"
                      >
                        <rect width="20" height="20" fill="#D9D9D9" />
                      </mask>
                      <g mask="url(#mask0_3324_15189)">
                        <path d="M16.6641 15.8333C16.428 15.8333 16.23 15.7535 16.0703 15.5938C15.9106 15.434 15.8307 15.2361 15.8307 15V12.5C15.8307 11.8056 15.5877 11.2153 15.1016 10.7292C14.6155 10.2431 14.0252 10 13.3307 10H5.66406L8.10156 12.4375C8.25434 12.5903 8.33073 12.7778 8.33073 13C8.33073 13.2222 8.2474 13.4167 8.08073 13.5833C7.92795 13.7361 7.73351 13.8125 7.4974 13.8125C7.26128 13.8125 7.06684 13.7361 6.91406 13.5833L3.08073 9.75C2.9974 9.66667 2.93837 9.57639 2.90365 9.47917C2.86892 9.38194 2.85156 9.27778 2.85156 9.16667C2.85156 9.05556 2.86892 8.95139 2.90365 8.85417C2.93837 8.75694 2.9974 8.66667 3.08073 8.58333L6.9349 4.72917C7.08767 4.57639 7.27517 4.5 7.4974 4.5C7.71962 4.5 7.91406 4.58333 8.08073 4.75C8.23351 4.90278 8.3099 5.09722 8.3099 5.33333C8.3099 5.56944 8.23351 5.76389 8.08073 5.91667L5.66406 8.33333H13.3307C14.4835 8.33333 15.4661 8.73958 16.2786 9.55208C17.0911 10.3646 17.4974 11.3472 17.4974 12.5V15C17.4974 15.2361 17.4175 15.434 17.2578 15.5938C17.0981 15.7535 16.9002 15.8333 16.6641 15.8333Z" />
                      </g>
                    </svg>
                    <div>Reply</div>
                  </Button>
                </div>

                {showFlagged && <FlaggedTag label="Comment flagged" />}
              </div>
            </td>
          </CommentWrapper>

          <tr>
            <td />
            <td>{repliesChild}</td>
          </tr>
        </table>
      </div>
      <LazyLoadedDeleteConfirmationModal
        isOpen={deleteConfirmationModalIsShown}
        onDelete={() => onDelete({ commentData: data })}
        onClose={hideDeleteConfirmationModal}
      />
      <LazyLoadedLikedByMembersListModal
        creatorUuid={creatorUuid}
        fetchCdnAsset={fetchCdnAsset}
        {...lazyLoadedLikedByMembersListModalProps}
      />
      <LazyLoadedImageViewerModal
        isOpen={isImageViewerModalOpen}
        onClose={onCloseImageViewerModal}
        imgSrc={imageViewerModalSrc}
        cdnBaseUrl={cdnBaseUrl}
      />
    </>
  );
};

export default Comment;
